import React, { useLayoutEffect, useRef } from "react";
import { parseCardText } from "../lib/cardTextFormat";

/** Textarea qui grandit avec son contenu — jamais de scrollbar interne qui
 * coupe le texte pendant la relecture/traduction. */
function AutoGrowTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);

  return <textarea {...props} ref={ref} rows={2} />;
}

export interface FaceFields {
  name?: string;
  subtitle?: string;
  title?: string;
  type?: string;
  traits?: string;
  text?: string;
  flavor_text?: string;
  level?: string;
  illustration?: string;
  entries?: Array<{ token: string; text: string }>;
  difficulty?: string;
}

/** Libellés lisibles pour les types de carte Shoggoth rencontrés — voir
 * types.ts. Sentence case (pas de MAJUSCULES) : cf. skill frontend-design,
 * les eyebrows tout en capitales sont un tic générique à éviter. */
export const CARD_TYPE_LABELS: Record<string, string> = {
  story: "Histoire",
  enemy: "Ennemi",
  enemy_location: "Lieu-ennemi",
  location: "Lieu",
  treachery: "Trahison",
  asset: "Soutien",
  event: "Événement",
  skill: "Compétence",
  act: "Acte",
  agenda: "Intrigue",
  investigator: "Investigateur",
  chaos: "Jeton chaos",
  scenario: "Scénario",
  key: "Clé",
  encounter: "Rencontre",
};

/** Une face n'a rien à montrer si elle n'a ni nom propre, ni texte d'aucune
 * sorte — cas fréquent des dos "génériques" (juste une illustration, un type
 * et des paramètres de cadrage). Utilisé par le parent pour masquer
 * entièrement le Verso plutôt que d'afficher un bloc vide. */
export function faceHasContent(face: FaceFields | undefined): boolean {
  if (!face) return false;
  return !!(
    face.name ||
    face.subtitle ||
    face.title ||
    face.traits ||
    face.text ||
    face.flavor_text ||
    (face.entries && face.entries.length > 0)
  );
}

/**
 * Rendu HTML/CSS "fidèle mais pas pixel-perfect" d'une face de carte — sert à
 * la navigation/relecture/traduction rapide ; l'aperçu image réel produit par
 * le conteneur Shoggoth reste disponible via RenderPreview.tsx.
 *
 * `cardName` : nom de la carte au niveau racine du JSON Shoggoth (le champ
 * `name` d'une face n'existe que pour certaines faces — ex. le dos d'une
 * carte "story" qui se retourne en ennemi nommé différemment du recto). Sert
 * de repli pour l'affichage ET pour la substitution du tag <name>.
 *
 * `showIllustration` : à false pour supprimer une image strictement
 * identique à celle déjà montrée sur l'autre face (cas fréquent des lieux,
 * une seule illustration recto/verso) plutôt que de l'afficher deux fois.
 */
export function CardFaceRenderer({
  face,
  cardName,
  editable = false,
  onFieldChange,
  emptyPlaceholder = "—",
  showIllustration = true,
  faceLabel,
}: {
  face: FaceFields;
  cardName?: string;
  editable?: boolean;
  onFieldChange?: (field: keyof FaceFields, value: string) => void;
  emptyPlaceholder?: string;
  showIllustration?: boolean;
  /** "Recto" / "Verso" — affiché comme repère discret, pas comme titre lourd. */
  faceLabel?: string;
}) {
  const effectiveName = face.name ?? cardName ?? "";
  const typeLabel = face.type ? CARD_TYPE_LABELS[face.type] ?? face.type : null;

  function Field({
    field,
    label,
    multiline = false,
    substituteName = false,
  }: {
    field: keyof FaceFields;
    label: string;
    multiline?: boolean;
    substituteName?: boolean;
  }) {
    const value = (face[field] as string) ?? "";
    if (!editable) {
      if (!value) return null;
      const parsed = multiline || substituteName ? parseCardText(value, effectiveName) : value;
      return <span className={`card-field card-field--${field}`}>{parsed}</span>;
    }
    return (
      <label className={`card-field-editable card-field-editable--${field}`}>
        <span className="card-field-editable__label">{label}</span>
        {multiline ? (
          <AutoGrowTextarea
            className="card-field-input"
            value={value}
            onChange={(e) => onFieldChange?.(field, e.target.value)}
          />
        ) : (
          <input className="card-field-input" value={value} onChange={(e) => onFieldChange?.(field, e.target.value)} />
        )}
      </label>
    );
  }

  return (
    <article className={`card-face card-face--${face.type ?? "generic"}`}>
      {faceLabel && <div className="card-face__face-label">{faceLabel}</div>}

      {showIllustration && face.illustration && (
        <div className="card-face__illustration">
          <img src={face.illustration} alt="" loading="lazy" />
        </div>
      )}

      <div className="card-face__content">
        <header className="card-face__header">
          {typeLabel && <span className="card-face__type-badge">{typeLabel}</span>}
          <h3 className="card-face__name">
            {editable ? (
              <Field field="name" label="Nom (laisser vide pour reprendre le nom de la carte)" />
            ) : (
              face.name ?? <span className="card-field--muted">{cardName || emptyPlaceholder}</span>
            )}
          </h3>
          {(face.subtitle || editable) && (
            <p className="card-face__subtitle">
              <Field field="subtitle" label="Sous-titre" />
            </p>
          )}
        </header>

        {(face.traits || editable) && (
          <p className="card-face__traits">
            <Field field="traits" label="Traits" substituteName />
          </p>
        )}

        {(face.text || editable) && (
          <div className="card-face__body">
            <Field field="text" label="Texte" multiline substituteName />
          </div>
        )}

        {face.entries && face.entries.length > 0 && (
          <ul className="card-face__entries">
            {face.entries.map((entry, i) => (
              <li key={i} className="card-face__entry">
                <span className={`card-icon card-icon--${entry.token}`} title={entry.token} />
                <span>{parseCardText(entry.text, effectiveName)}</span>
              </li>
            ))}
          </ul>
        )}
        {face.difficulty && <p className="card-face__difficulty">{face.difficulty}</p>}

        {(face.flavor_text || editable) && (
          <div className="card-face__flavor">
            <Field field="flavor_text" label="Texte d'ambiance" multiline substituteName />
          </div>
        )}
      </div>
    </article>
  );
}
